import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { getRepertoireForSong } from './RepertoireService';
import { fetchTracksForStudent } from './StudentTracksService';
import { pointerDocId, pointerFor, reuseSources, songPatchFor } from './songReuseModel';

/**
 * Reads and the one write behind "use what another student already has"
 * (songReuseModel.js says what is copied and what never is).
 *
 * Reads: one equality query on the registry id (no composite index), then
 * ONE pointer query per other student, the shape fetchTracksForStudent
 * already uses, capped so a song half the studio sings costs a handful of
 * reads rather than dozens.
 */
const MAX_STUDENTS = 6;

export const findReuseSources = async ({ canonicalSongId, studentId }) => {
	if (!canonicalSongId || !studentId) return [];
	const rows = (await getRepertoireForSong(canonicalSongId)).filter((r) => r.studentId && r.studentId !== studentId);
	if (!rows.length) return [];
	const ms = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : 0);
	const studentIds = [...new Set(rows.sort((a, b) => ms(b.lastWorkedOn) - ms(a.lastWorkedOn)).map((r) => r.studentId))].slice(0, MAX_STUDENTS);
	const [trackLists, studentSnaps] = await Promise.all([
		Promise.all(studentIds.map((id) => fetchTracksForStudent(id).catch(() => []))),
		Promise.all(studentIds.map((id) => getDoc(doc(db, 'students', id)).catch(() => null))),
	]);
	const tracksByStudent = {};
	const names = {};
	studentIds.forEach((id, i) => {
		tracksByStudent[id] = trackLists[i];
		const s = studentSnaps[i] && studentSnaps[i].exists() ? studentSnaps[i].data() : null;
		names[id] = (s && (s.firstName || (s.name && String(s.name).split(' ')[0]))) || null;
	});
	return reuseSources({ rows: rows.filter((r) => studentIds.includes(r.studentId)), tracksByStudent, names, studentId });
};

/**
 * Copy one source onto the new student's song in ONE batch: the repertoire
 * row (dot paths, only what it lacks) and the track pointer (the same files,
 * no new audio). `song` is the board song ({ id, name, canonicalSongId,
 * lyrics, videos, sections, sectionMarks }). Returns { patch, brought,
 * tracksAdded } so the page can update the board without a reload.
 */
export const applyReuse = async ({ source, song, studentId, studentName = null }) => {
	if (!source || !song || !song.id || !studentId) throw new Error('Nothing to copy.');
	const { patch, brought } = songPatchFor(source, song);
	const pointerId = pointerDocId(studentId, song.name);
	const pointerRef = doc(db, 'studentTracks', pointerId);
	const existingSnap = await getDoc(pointerRef);
	const existing = existingSnap.exists() ? existingSnap.data() : null;
	// A pointer an admin hid with the song cannot be reopened by a teacher
	// (removedAt is admin-only), and files added to it would be invisible.
	const pointer = existing && existing.removedAt ? null : pointerFor({ studentId, studentName, song, files: source.files, existing });

	const batch = writeBatch(db);
	if (Object.keys(patch).length) batch.update(doc(db, 'repertoire', song.id), { ...patch, updatedAt: serverTimestamp() });
	if (pointer) {
		const { added, ...data } = pointer;
		if (existing) batch.update(pointerRef, { files: data.files, updatedAt: serverTimestamp() });
		else batch.set(pointerRef, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
	}
	if (!Object.keys(patch).length && !pointer) return { patch, brought, tracksAdded: 0 };
	await batch.commit();
	return { patch, brought, tracksAdded: pointer ? pointer.added : 0 };
};
